import { z } from 'zod';

/* ───────────────────────── Сверка счёта с договором и приходом (ФН-20) ───────────────────────── */

/** Допуск сверки задаёт администратор; по умолчанию 0 % (ФН-20) */
export const MatchSettings = z.object({
  /** Сверка включена: расхождение сверх допуска останавливает заявку на оплату */
  enabled: z.boolean(),
  /** Допуск по цене, % от цены спецификации */
  pricePct: z.number().min(0).max(100),
  /** Допуск по количеству, % от количества спецификации и прихода */
  qtyPct: z.number().min(0).max(100),
});
export type MatchSettings = z.infer<typeof MatchSettings>;
export const MATCH_DEFAULTS: MatchSettings = { enabled: true, pricePct: 0, qtyPct: 0 };

export const PaymentInvoiceLineInput = z.object({
  name: z.string().trim().min(1).max(300),
  unit: z.string().trim().min(1).max(30),
  quantity: z.number().positive().max(1e9),
  price: z.number().min(0).max(1e12),
  contractItemId: z.uuid().nullable().optional(),
});
export type PaymentInvoiceLineInput = z.infer<typeof PaymentInvoiceLineInput>;

export const PaymentInvoiceInput = z.object({ lines: z.array(PaymentInvoiceLineInput).max(300) });
export type PaymentInvoiceInput = z.infer<typeof PaymentInvoiceInput>;

export const PaymentInvoiceLineView = z.object({
  id: z.uuid(),
  name: z.string(),
  unit: z.string(),
  quantity: z.number(),
  /** Цена скрыта без уровня денег «Акты и счета» (ДС-04) */
  price: z.number().nullable(),
  contractItemId: z.uuid().nullable(),
});
export type PaymentInvoiceLineView = z.infer<typeof PaymentInvoiceLineView>;

export const MatchRow = z.object({
  name: z.string(),
  unit: z.string(),
  contractQty: z.number().nullable(),
  contractPrice: z.number().nullable(),
  invoiceQty: z.number(),
  invoicePrice: z.number().nullable(),
  /** Выставлено по другим заявкам на оплату этого договора (переданным и оплаченным) */
  invoicedBefore: z.number(),
  /** Принято на склад по договору; null — приход не нужен (аванс) */
  receivedQty: z.number().nullable(),
  issues: z.array(z.string()),
});
export type MatchRow = z.infer<typeof MatchRow>;

export const MatchResult = z.object({
  /** ok — совпало, mismatch — расхождение сверх допуска, not_required — сверка не нужна */
  status: z.enum(['ok', 'mismatch', 'not_required']),
  /** Почему сверка не нужна или чего не хватает для неё */
  note: z.string().nullable(),
  rows: z.array(MatchRow),
  issues: z.array(z.string()),
  invoiceTotal: z.number().nullable(),
  pricePct: z.number(),
  qtyPct: z.number(),
  checkedAt: z.string().nullable(),
});
export type MatchResult = z.infer<typeof MatchResult>;

/* ───────────────────────── Лимиты согласования (ДС-06) ───────────────────────── */

export const APPROVAL_ACTIONS = {
  purchase_approval: 'Согласование выбора поставщика по заявке на закуп',
  payment_send: 'Передача заявки на оплату',
  contract_approval: 'Согласование договора',
  amendment_approval: 'Согласование допсоглашения',
} as const;
export type ApprovalAction = keyof typeof APPROVAL_ACTIONS;
const ApprovalActionSchema = z.enum(Object.keys(APPROVAL_ACTIONS) as [ApprovalAction, ...ApprovalAction[]]);

export const ApprovalLimitInput = z
  .object({
    action: ApprovalActionSchema,
    roleId: z.uuid().nullable().optional(),
    userId: z.uuid().nullable().optional(),
    /** Предел суммы одного решения; пусто — без ограничения */
    maxAmount: z.number().min(0).max(1e13).nullable().optional(),
    /** Предел превышения сметы, %; пусто — без ограничения. Для передачи на оплату не применяется */
    maxDeviationPct: z.number().min(0).max(1000).nullable().optional(),
    note: z.string().trim().max(500).nullable().optional(),
  })
  .refine((v) => Boolean(v.roleId) !== Boolean(v.userId), { message: 'Укажите роль или человека' })
  .refine((v) => v.maxAmount != null || v.maxDeviationPct != null, {
    message: 'Задайте предел суммы или отклонения от сметы',
  });
export type ApprovalLimitInput = z.infer<typeof ApprovalLimitInput>;

export const ApprovalLimitView = z.object({
  id: z.uuid(),
  action: ApprovalActionSchema,
  roleId: z.uuid().nullable(),
  roleName: z.string().nullable(),
  userId: z.uuid().nullable(),
  userName: z.string().nullable(),
  maxAmount: z.number().nullable(),
  maxDeviationPct: z.number().nullable(),
  note: z.string().nullable(),
});
export type ApprovalLimitView = z.infer<typeof ApprovalLimitView>;

/** Ответ портала, когда решение выше лимита: кто решает дальше */
export const AboveLimit = z.object({
  action: ApprovalActionSchema,
  reasons: z.array(z.string()),
  nextApprovers: z.array(z.object({ userId: z.uuid(), name: z.string() })),
});
export type AboveLimit = z.infer<typeof AboveLimit>;

/* ───────────────────────── Выгрузка в 1С (ИН-05) ───────────────────────── */

/**
 * Поля портала, которые можно поставить в колонку файла. Поля обмена с 1С не согласованы с бухгалтерией:
 * портал отдаёт свои поля, а названия колонок и порядок настраивает компания в профиле выгрузки.
 */
export const EXPORT_FIELDS = {
  number: { label: 'Номер заявки на оплату', type: 'text' },
  plannedDate: { label: 'Плановая дата оплаты', type: 'date' },
  amount: { label: 'Сумма', type: 'number' },
  currency: { label: 'Валюта', type: 'text' },
  kind: { label: 'Тип платежа', type: 'text' },
  status: { label: 'Статус заявки', type: 'text' },
  organization: { label: 'Организация (юрлицо проекта)', type: 'text' },
  organizationBin: { label: 'БИН организации', type: 'text' },
  projectCode: { label: 'Шифр проекта', type: 'text' },
  projectName: { label: 'Название проекта', type: 'text' },
  counterpartyName: { label: 'Контрагент', type: 'text' },
  counterpartyBin: { label: 'БИН контрагента', type: 'text' },
  contractNumber: { label: 'Номер договора', type: 'text' },
  contractDate: { label: 'Дата договора', type: 'date' },
  costArticle: { label: 'Статья ФП', type: 'text' },
  invoiceNumber: { label: 'Номер счёта поставщика', type: 'text' },
  invoiceDate: { label: 'Дата счёта поставщика', type: 'date' },
  purpose: { label: 'Назначение платежа', type: 'text' },
  requestNumber: { label: 'Номер заявки на закуп', type: 'text' },
  registryNumber: { label: 'Номер реестра', type: 'text' },
  priority: { label: 'Приоритет', type: 'text' },
  fundingSource: { label: 'Источник финансирования', type: 'text' },
  oneCNumber: { label: 'Номер в 1С', type: 'text' },
  oneCDate: { label: 'Дата в 1С', type: 'date' },
  oneCStatus: { label: 'Статус в 1С', type: 'text' },
  note: { label: 'Примечание', type: 'text' },
  id: { label: 'Идентификатор записи в портале', type: 'text' },
  /** Постоянное значение во всех строках (например, вид операции): задаётся в колонке */
  constant: { label: 'Постоянное значение', type: 'text' },
} as const;
export type ExportField = keyof typeof EXPORT_FIELDS;
const ExportFieldSchema = z.enum(Object.keys(EXPORT_FIELDS) as [ExportField, ...ExportField[]]);

export const ExportColumn = z
  .object({
    field: ExportFieldSchema,
    /** Заголовок колонки в файле — как его ждёт 1С */
    header: z.string().trim().min(1).max(80),
    /** Значение для поля «constant» */
    value: z.string().trim().max(200).nullable().optional(),
  })
  .refine((c) => c.field !== 'constant' || c.value != null, { message: 'Укажите постоянное значение' });
export type ExportColumn = z.infer<typeof ExportColumn>;

export const EXPORT_FORMATS = { csv: 'CSV', xlsx: 'Excel', json: 'JSON (для интерфейса обмена)' } as const;
export type ExportFormat = keyof typeof EXPORT_FORMATS;

export const ExportProfileInput = z.object({
  name: z.string().trim().min(2).max(80),
  format: z.enum(['csv', 'xlsx', 'json']).default('csv'),
  delimiter: z.enum([';', ',', '\t']).default(';'),
  locale: z.enum(['ru', 'iso']).default('ru'),
  columns: z.array(ExportColumn).min(1).max(60),
  /** Статусы заявок на оплату, которые попадают в выгрузку */
  statuses: z
    .array(z.enum(['sent', 'paid']))
    .min(1)
    .default(['sent']),
  active: z.boolean().default(true),
});
export type ExportProfileInput = z.infer<typeof ExportProfileInput>;

export const ExportProfileView = z.object({
  id: z.uuid(),
  name: z.string(),
  entity: z.literal('payment_requests'),
  format: z.enum(['csv', 'xlsx', 'json']),
  delimiter: z.enum([';', ',', '\t']),
  locale: z.enum(['ru', 'iso']),
  columns: z.array(ExportColumn),
  statuses: z.array(z.enum(['sent', 'paid'])),
  active: z.boolean(),
});
export type ExportProfileView = z.infer<typeof ExportProfileView>;

export const ExportQuery = z.object({
  profileId: z.uuid(),
  projectId: z.uuid().optional(),
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  /** Только ещё не выгруженные (по умолчанию) или все, включая выгруженные ранее */
  includeExported: z.enum(['0', '1']).default('0'),
});
export type ExportQuery = z.infer<typeof ExportQuery>;

export const ExportBatchView = z.object({
  id: z.uuid(),
  profileName: z.string(),
  format: z.string(),
  rows: z.number().int(),
  total: z.number().nullable(),
  at: z.string(),
  byName: z.string().nullable(),
});
export type ExportBatchView = z.infer<typeof ExportBatchView>;

/** Сводка перед выгрузкой: сколько заявок войдёт и почему часть не войдёт */
export const ExportPreview = z.object({
  rows: z.number().int(),
  total: z.number().nullable(),
  alreadyExported: z.number().int(),
  skipped: z.array(z.object({ number: z.string(), reason: z.string() })),
});
export type ExportPreview = z.infer<typeof ExportPreview>;
